"use client";

import { ChevronRight, type LucideIcon } from "lucide-react";

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
    SidebarGroup,
    SidebarMenu,
    SidebarMenuAction,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarMenuSub,
    SidebarMenuSubButton,
    SidebarMenuSubItem,
} from "@/components/ui/sidebar";
import { useNavigate } from "react-router";
import { PermissionDto } from "@exibra/shared";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

export function NavMain({
    items,
}: {
    items: {
        title: string;
        url: string;
        icon: LucideIcon;
        isActive?: boolean;
        items?: {
            title: string;
            url: string;
        }[];
        enabled: boolean;
    }[];
    userAccess: PermissionDto[];
}) {
    const navigate = useNavigate();
    const { t } = useTranslation();

    return (
        <SidebarGroup>
            <SidebarMenu>
                {items
                    .filter((item) => item.enabled)
                    .map((item) => (
                        <Collapsible key={item.title} asChild defaultOpen={item.isActive}>
                            <SidebarMenuItem>
                                <SidebarMenuButton
                                    asChild
                                    tooltip={item.title}
                                    isActive={item.isActive}
                                    className={cn(
                                        item.isActive &&
                                            "relative before:absolute before:left-0 before:top-1/2 before:h-4 before:w-[3px] before:-translate-y-1/2 before:rounded-r-full before:bg-brand-lime-2",
                                    )}
                                >
                                    <button
                                        onClick={(e) => {
                                            e.preventDefault();
                                            navigate(item.url);
                                        }}
                                    >
                                        <item.icon />
                                        <span>{item.title}</span>
                                    </button>
                                </SidebarMenuButton>
                                {item.items?.length ? (
                                    <>
                                        <CollapsibleTrigger asChild>
                                            <SidebarMenuAction className="data-[state=open]:rotate-90">
                                                <ChevronRight />
                                                <span className="sr-only">
                                                    {t("general.toggleSubmenu")}
                                                </span>
                                            </SidebarMenuAction>
                                        </CollapsibleTrigger>
                                        <CollapsibleContent>
                                            <SidebarMenuSub>
                                                {item.items?.map((subItem) => (
                                                    <SidebarMenuSubItem key={subItem.title}>
                                                        <SidebarMenuSubButton asChild>
                                                            <button
                                                                onClick={() =>
                                                                    navigate(subItem.url)
                                                                }
                                                            >
                                                                <span>{subItem.title}</span>
                                                            </button>
                                                        </SidebarMenuSubButton>
                                                    </SidebarMenuSubItem>
                                                ))}
                                            </SidebarMenuSub>
                                        </CollapsibleContent>
                                    </>
                                ) : null}
                            </SidebarMenuItem>
                        </Collapsible>
                    ))}
            </SidebarMenu>
        </SidebarGroup>
    );
}
